// Platform Admin — Governance Gates + Automation Hooks, combined on one page (mirrors the
// mockup's P4 screens). Linked from the Builder's "Governance & Hooks" button. Reuses the same
// governance_gates/automation_hooks arrays and update mutation the Builder round-trips — no new
// data shape, just a dedicated page + modal UX instead of a flat section inside the old editor.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { Modal } from '@/components/UI/Modal';
import {
  useGetWorkflowDefinitionQuery,
  useUpdateWorkflowDefinitionMutation,
  CreateWorkflowRequest,
  GovernanceGate,
  ApprovalType,
  AutomationHook,
  ModuleDisplay,
} from '@/redux/internal-services/workflowApi';
import { ROLE_OPTIONS, humanize } from '@/components/Workflow/workflowSettingsShared';
import { useListGovernanceActionsQuery, usePreviewGovernanceOptionsQuery, hasCapability, GovernanceActionOption, ModuleCapability } from '@/redux/internal-services/workflowGovernanceApi';

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-ui-text2 mb-1">
    {children}{required && <span className="text-danger-text ml-1">*</span>}
  </label>
);
const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }) => (
  <input {...props} className={cn(fieldClass(), className)} />
);
const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...props }) => (
  <select {...props} className={cn(fieldClass(), className)} />
);

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const CONDITION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'required_data', label: 'Required data — field present' },
  { value: 'required_document', label: 'Document — files attached' },
  { value: 'task_completion', label: 'Required task — task complete' },
  { value: 'approval', label: 'Approval — requires sign-off' },
  { value: 'business_validation', label: 'Platform validation — built-in rule' },
  { value: 'required_business_capture', label: 'Business action — capture required before proceeding' },
];

// Values MUST match app.domain.workflows.enums.TriggerPoint exactly — the backend rejects
// anything else with a 422 (confirmed live: the previous 'on_enter'/'on_exit'/'on_dwell' values
// meant EVERY hook ever added through this modal failed to save, regardless of action_type —
// not a module_action-specific issue). There is no dwell-time trigger point on the backend at
// all; 'transition_complete' (fires when a named transition finishes, bound to a transition
// code rather than a status code) was the real third option missing here.
const TRIGGER_POINTS: Array<{ value: string; label: string }> = [
  { value: 'status_entry', label: 'On enter status' },
  { value: 'status_exit', label: 'On exit status' },
  { value: 'transition_complete', label: 'On transition complete' },
];

// Values MUST match app.domain.workflows.enums.AutomationActionType exactly — the backend's
// _dispatch_hook only recognizes these 5 and silently records any other value as a failed
// outcome ("Unknown action_type '...'"), non-mandatory hooks swallow that error so the
// transition itself still succeeds, which makes a typo'd value here easy to miss without
// checking workflow_automation_outcomes directly. 'module_action' resolves through the SAME
// registry (GET /workflows/governance-actions) the Required Data gate's on_captured builder
// below already uses — picking an unregistered key is impossible by construction (dropdown-only).
const ACTION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'task_creation', label: 'Create task' },
  { value: 'follow_up', label: 'Create follow-up task' },
  { value: 'notification', label: 'Send notification' },
  { value: 'approval_creation', label: 'Request approval' },
  { value: 'module_action', label: 'Invoke module action' },
];

// ─────────────────────────── Required-data capture + post-action builder ──
// WFL-FR-03-007/008 — "1. What we ask for" (per-field capture spec) + "2. What happens once it
// is answered" (persist is automatic/always-on; the backend action + wait_for_it/fire_and_forget
// split is what's configured here). condition_config.fields stays the list of required field
// keys (unchanged, backward-compatible with older gates); capture/on_captured are additive.

// Both take `t` rather than calling useTranslation() themselves — they're plain functions used
// inside component bodies (a module-level constant can't call a hook).
const inputTypes = (t: (key: string) => string): Array<{ value: string; label: string }> => [
  { value: 'text', label: t('workflow.settings.requiredDataBuilder.inputTypeText') },
  { value: 'number', label: t('workflow.settings.requiredDataBuilder.inputTypeNumber') },
  { value: 'currency', label: t('workflow.settings.requiredDataBuilder.inputTypeCurrency') },
  { value: 'date', label: t('workflow.settings.requiredDataBuilder.inputTypeDate') },
  { value: 'dropdown', label: t('workflow.settings.requiredDataBuilder.inputTypeDropdown') },
  { value: 'multi_select', label: t('workflow.settings.requiredDataBuilder.inputTypeMultiSelect') },
  { value: 'file', label: t('workflow.settings.requiredDataBuilder.inputTypeFile') },
  { value: 'yes_no', label: t('workflow.settings.requiredDataBuilder.inputTypeYesNo') },
];

const optionSources = (t: (key: string) => string): Array<{ value: string; label: string }> => [
  { value: '', label: t('workflow.settings.requiredDataBuilder.optionSourceNotApplicable') },
  { value: 'static', label: t('workflow.settings.requiredDataBuilder.optionSourceFixedList') },
  { value: 'role_lookup', label: t('workflow.settings.requiredDataBuilder.optionSourceRoleLookup') },
];

interface CaptureFieldRow {
  // No separate "field key" input — the mockup only ever shows a Field Label; the technical
  // condition_config.fields key is derived from it via slug() at save time (same pattern this
  // file already uses for gate_code from the condition name).
  label: string;
  input_type: string;
  captured_by_role: string;
  options_source: string;
  options_value: string; // comma-separated options (static) or a role code (role_lookup)
  mandatory: boolean;
  tenant_relabel_allowed: boolean;
}

const emptyCaptureRow = (): CaptureFieldRow => ({
  label: '', input_type: 'text', captured_by_role: '',
  options_source: '', options_value: '', mandatory: true, tenant_relabel_allowed: false,
});

const stepCardClass = (active: boolean) =>
  `text-left px-3 py-2 rounded-control border transition ${active ? 'border-accent bg-accent-soft' : 'border-ui-border hover:bg-ui-hover'}`;

// "Resolves at runtime to" live preview (WFL-FR-03-007) for a role_lookup options source — never
// a frozen list, re-queried whenever the role selection changes.
const RoleLookupPreview: React.FC<{ role: string }> = ({ role }) => {
  const { t } = useTranslation();
  const { data, isFetching } = usePreviewGovernanceOptionsQuery({ role }, { skip: !role });
  if (!role) return null;
  if (isFetching) return <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.resolving')}</p>;
  const preview = data?.data;
  if (!preview) return null;
  if (!preview.available) {
    return (
      <p className="text-xs text-ui-text3">
        {t('workflow.settings.requiredDataBuilder.resolvesPerTenantNoPreview')}
      </p>
    );
  }
  const shown = preview.options.slice(0, 3);
  const extra = preview.options.length - shown.length;
  return (
    <div className="rounded-control border border-ui-border bg-ui-subtle p-2">
      <p className="text-micro uppercase tracking-wide text-ui-text3 mb-1.5">{t('workflow.settings.requiredDataBuilder.resolvesAtRuntimeTo')}</p>
      {preview.options.length === 0 ? (
        <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.noActiveMembers')}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {shown.map((o) => (
            <Badge key={o.value} variant="neutral">{o.label}</Badge>
          ))}
          {extra > 0 && <Badge variant="neutral">{t('workflow.settings.requiredDataBuilder.extraActive', { count: extra })}</Badge>}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── Business module picker ───────────────────────
// ONE picker for all four binding builders (required_data on_captured, required_business_capture
// gate, module_action hook, module_displays status binding). Previously each built its own
// <Select> inline and they disagreed in ways that produced silently-broken bindings: only the
// module_displays one filtered at all, so a display-only card like `estimate_summary_panel`
// appeared in the hook picker (binding it no-ops forever, since no callable is registered) and in
// the capture-gate picker (the transition then blocks on a condition that can never be reported
// satisfied). Centralised here so "may this module be bound as X?" is answered in exactly one
// place — see hasCapability in workflowGovernanceApi.
//
// Options are labelled with `name`, not `action_key`: the key is an identifier, and reading
// "meeting_schedule_for_assessment" in a dropdown is worse than "Schedule Assessment Meeting".
// Grouped by `category`, whose vocabulary is config-driven
// (workflow_engine.business_module_categories) and so is derived from the data here rather than
// hardcoded — a category added in the catalogue shows up without a frontend change.
// Order and copy for capability chips. "Can be bound as" is the admin's question, so the labels
// name the BINDING each capability unlocks rather than the internal capability word.
const CAPABILITY_ORDER: ModuleCapability[] = ['action', 'display', 'capture'];
const CAPABILITY_LABELS: Record<ModuleCapability, string> = {
  action: 'backend action',
  display: 'status display',
  capture: 'capture form',
};

// ─────────────────────── Module input fields (contract-driven) ────────────
// Renders a module's DECLARED admin-suppliable inputs (business_modules.module_inputs). Shared by
// the automation-hook builder and the required_business_capture gate builder, because both store
// values against the same contract — the hook in action_config.input_values, the gate in
// condition_config.input_values — and a second implementation would drift from the first.
//
// Runtime inputs are excluded: those are collected from the user when the module runs, so an
// admin has no value to supply and hardcoding one would be wrong (a fixed assessment start_time
// books every future meeting at one instant in the past).
type ModuleInputValue = string | string[];

const ModuleInputFields: React.FC<{
  module?: GovernanceActionOption;
  values: Record<string, ModuleInputValue>;
  onChange: (values: Record<string, ModuleInputValue>) => void;
}> = ({ module, values, onChange }) => {
  if (!module) return null;
  const adminInputs = (module.module_inputs ?? []).filter((i) => !i.runtime);
  if (adminInputs.length === 0) return null;

  const set = (name: string, v: ModuleInputValue) => onChange({ ...values, [name]: v });

  return (
    <div className="space-y-2">
      <Label>Settings for this module</Label>
      <p className="text-xs text-ui-text3">
        Declared by the module itself, so these are the values it actually reads — not free-typed
        keys that may never be looked at.
      </p>
      {adminInputs.map((input) => {
        const raw = values[input.name];
        const isMultiSelect = input.type === 'list' && input.allowed_values.length > 0;
        const selected: string[] = Array.isArray(raw) ? raw : [];

        return (
          <div key={input.name}>
            <div className="flex items-start gap-2">
              <span className="w-40 shrink-0 pt-1.5 font-mono text-xs text-ui-text2">
                {input.name}{input.required ? ' *' : ''}
              </span>
              <div className="flex-1">
                {isMultiSelect ? (
                  /* A list with a fixed vocabulary is a set of choices, not a comma-separated
                     string an admin has to punctuate correctly — and the backend validates each
                     element against these same allowed_values. */
                  <div className="flex flex-wrap gap-2">
                    {input.allowed_values.map((v) => {
                      const value = String(v);
                      const on = selected.includes(value);
                      return (
                        <label key={value} className="flex items-center gap-1 text-xs text-ui-text2">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => set(
                              input.name,
                              on ? selected.filter((x) => x !== value) : [...selected, value],
                            )}
                          />
                          {value}
                        </label>
                      );
                    })}
                  </div>
                ) : input.allowed_values.length > 0 ? (
                  <Select
                    value={typeof raw === 'string' ? raw : ''}
                    onChange={(e) => set(input.name, e.target.value)}
                  >
                    <option value="">{input.default != null ? String(input.default) : '—'}</option>
                    {input.allowed_values.map((v) => (
                      <option key={String(v)} value={String(v)}>{String(v)}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    value={typeof raw === 'string' ? raw : ''}
                    onChange={(e) => set(input.name, e.target.value)}
                    type={input.type === 'int' || input.type === 'float' ? 'number' : 'text'}
                    placeholder={input.default != null ? `default: ${JSON.stringify(input.default)}` : input.type}
                  />
                )}
                {input.description && (
                  <p className="mt-0.5 text-xs text-ui-text3">{input.description}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Drops blanks so a module's own documented default applies, instead of sending "" and
 *  defeating it. */
const cleanModuleInputs = (values: Record<string, ModuleInputValue>): Record<string, ModuleInputValue> =>
  Object.fromEntries(
    Object.entries(values).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v.trim() !== '')),
  );

const ModulePicker: React.FC<{
  /** The capability this binding kind REQUIRES. Modules lacking it are not offered. */
  capability: ModuleCapability;
  value: string;
  onChange: (v: string) => void;
  /** Extra note rendered per option, e.g. flagging a missing inline capture form. */
  annotate?: (m: GovernanceActionOption) => string;
}> = ({ capability, value, onChange, annotate }) => {
  const { data, isLoading } = useListGovernanceActionsQuery();
  const modules = (data?.data ?? []).filter((m) => hasCapability(m, capability));

  // Preserve the server's (category, name) ordering while grouping, so optgroup order is stable.
  const categories: string[] = [];
  modules.forEach((m) => {
    const c = m.category || 'other';
    if (!categories.includes(c)) categories.push(c);
  });

  const selected = modules.find((m) => m.action_key === value);
  // A module already bound but no longer offerable (capability lost, or it was deactivated) must
  // stay visible rather than silently resetting the field to blank on open.
  const orphaned = value && !selected;

  return (
    <>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{isLoading ? 'Loading…' : 'Select…'}</option>
        {orphaned && <option value={value}>{value} — not available for this binding</option>}
        {categories.map((cat) => (
          <optgroup key={cat} label={cat}>
            {modules.filter((m) => (m.category || 'other') === cat).map((m) => (
              <option key={m.action_key} value={m.action_key}>
                {m.name}{annotate?.(m) || ''}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      {orphaned && (
        <p className="mt-1 text-xs text-warning-text">
          This binding references <code>{value}</code>, which is no longer available as a{' '}
          {capability} module. It will not fire until the module is restored or the binding is changed.
        </p>
      )}
      {selected && (
        <>
          {selected.description && <p className="mt-1 text-xs text-ui-text3">{selected.description}</p>}
          {/* Catalogue-vs-registry disagreement, reported by the backend rather than trusted —
              a module claiming an action with no callable behind it is a broken binding waiting
              to happen, and the admin is the only one who can fix it. */}
          {selected.capability_drift?.length > 0 && selected.capability_drift.map((d) => (
            <p key={d} className="mt-1 text-xs text-warning-text">Catalogue mismatch: {d}</p>
          ))}
        </>
      )}
    </>
  );
};

const RequiredDataCaptureBuilder: React.FC<{
  captureFields: CaptureFieldRow[];
  setCaptureFields: (rows: CaptureFieldRow[]) => void;
  onCapturedEnabled: boolean;
  setOnCapturedEnabled: (v: boolean) => void;
  actionKey: string;
  setActionKey: (v: string) => void;
  inputMapping: Array<{ param: string; expr: string }>;
  setInputMapping: (rows: Array<{ param: string; expr: string }>) => void;
  mode: 'wait_for_it' | 'fire_and_forget';
  setMode: (v: 'wait_for_it' | 'fire_and_forget') => void;
  onFailure: 'block' | 'warn' | 'ignore';
  setOnFailure: (v: 'block' | 'warn' | 'ignore') => void;
  maxRetries: number;
  setMaxRetries: (v: number) => void;
  backoff: 'exponential' | 'fixed';
  setBackoff: (v: 'exponential' | 'fixed') => void;
}> = ({
  captureFields, setCaptureFields, onCapturedEnabled, setOnCapturedEnabled, actionKey, setActionKey,
  inputMapping, setInputMapping, mode, setMode, onFailure, setOnFailure, maxRetries, setMaxRetries, backoff, setBackoff,
}) => {
  const { t } = useTranslation();

  const updateRow = (i: number, patch: Partial<CaptureFieldRow>) =>
    setCaptureFields(captureFields.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-ui-border pt-3">
      {/* ── 1. What we ask for ──────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-ui-text2">{t('workflow.settings.requiredDataBuilder.whatWeAskForHeading')}</p>
          <p className="text-micro uppercase tracking-wide text-ui-text3">{t('workflow.settings.requiredDataBuilder.shownInAppAtGate')}</p>
        </div>
        {captureFields.map((row, i) => (
          <div key={i} className="rounded-control border border-ui-border p-2.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <Label>{t('workflow.settings.requiredDataBuilder.inputTypeLabel')}</Label>
              {captureFields.length > 1 && (
                <Button type="button" variant="destructive-ghost" size="xs" onClick={() => setCaptureFields(captureFields.filter((_, idx) => idx !== i))}>
                  {t('workflow.settings.requiredDataBuilder.remove')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {inputTypes(t).map((it) => (
                <Chip
                  key={it.value}
                  selected={row.input_type === it.value}
                  onClick={() => updateRow(i, { input_type: it.value })}
                  className="w-full justify-center py-1.5"
                >
                  {it.label}
                </Chip>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label required>{t('workflow.settings.requiredDataBuilder.fieldLabel')}</Label>
                <Input value={row.label} onChange={(e) => updateRow(i, { label: e.target.value })} placeholder={t('workflow.settings.requiredDataBuilder.fieldLabelPlaceholder')} />
              </div>
              <div>
                <Label>{t('workflow.settings.requiredDataBuilder.capturedBy')}</Label>
                <Select value={row.captured_by_role} onChange={(e) => updateRow(i, { captured_by_role: e.target.value })}>
                  <option value="">{t('workflow.settings.requiredDataBuilder.anyRole')}</option>
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
                </Select>
              </div>
            </div>
            {(row.input_type === 'dropdown' || row.input_type === 'multi_select') && (
              <div className="space-y-1.5 border-t border-ui-border2 pt-2">
                <Label>{t('workflow.settings.requiredDataBuilder.optionsSource')}</Label>
                <Select value={row.options_source} onChange={(e) => updateRow(i, { options_source: e.target.value, options_value: '' })}>
                  {optionSources(t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                {row.options_source === 'static' && (
                  <Input value={row.options_value} onChange={(e) => updateRow(i, { options_value: e.target.value })} placeholder={t('workflow.settings.requiredDataBuilder.staticOptionsPlaceholder')} />
                )}
                {row.options_source === 'role_lookup' && (
                  <>
                    <Select value={row.options_value} onChange={(e) => updateRow(i, { options_value: e.target.value })}>
                      <option value="">{t('workflow.settings.requiredDataBuilder.selectRolePlaceholder')}</option>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
                    </Select>
                    <RoleLookupPreview role={row.options_value} />
                    <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.notFrozenListHint')}</p>
                  </>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={row.mandatory} onChange={(e) => updateRow(i, { mandatory: e.target.checked })} /> {t('workflow.settings.requiredDataBuilder.mandatoryToggle')}
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={row.tenant_relabel_allowed} onChange={(e) => updateRow(i, { tenant_relabel_allowed: e.target.checked })} /> {t('workflow.settings.requiredDataBuilder.allowRelabelToggle')}
            </label>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setCaptureFields([...captureFields, emptyCaptureRow()])}>{t('workflow.settings.requiredDataBuilder.addField')}</Button>
      </div>

      {/* ── 2. What happens once it is answered ─────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-ui-text2">{t('workflow.settings.requiredDataBuilder.whatHappensHeading')}</p>
          <p className="text-micro uppercase tracking-wide text-ui-text3">{t('workflow.settings.requiredDataBuilder.runsInOrder')}</p>
        </div>

        <div className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-xs font-semibold flex items-center justify-center">1</span>
          <div>
            <p className="text-sm font-medium text-ui-text">{t('workflow.settings.requiredDataBuilder.persistTheValue')}</p>
            <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.persistDescription')}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-xs font-semibold flex items-center justify-center">2</span>
          <div className="flex-1 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-ui-text cursor-pointer">
              <input type="checkbox" checked={onCapturedEnabled} onChange={(e) => setOnCapturedEnabled(e.target.checked)} /> {t('workflow.settings.requiredDataBuilder.thenRunBackendAction')}
            </label>
            {onCapturedEnabled && (
              <div className="rounded-control border border-ui-border p-2.5 space-y-2">
                <Label required>{t('workflow.settings.requiredDataBuilder.actionRegisteredOnly')}</Label>
                {/* Executes the module, so it needs a registered callable — capability "action". */}
                <ModulePicker capability="action" value={actionKey} onChange={setActionKey} />

                <Label>{t('workflow.settings.requiredDataBuilder.inputMapping')}</Label>
                {inputMapping.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={m.param} onChange={(e) => setInputMapping(inputMapping.map((r, idx) => (idx === i ? { ...r, param: e.target.value } : r)))} placeholder={t('workflow.settings.requiredDataBuilder.paramPlaceholder')} className="flex-1" />
                    <span className="text-ui-text3 text-xs">←</span>
                    <Input value={m.expr} onChange={(e) => setInputMapping(inputMapping.map((r, idx) => (idx === i ? { ...r, expr: e.target.value } : r)))} placeholder={t('workflow.settings.requiredDataBuilder.exprPlaceholder')} className="flex-1" />
                    <Button type="button" variant="destructive-ghost" size="xs" className="shrink-0" onClick={() => setInputMapping(inputMapping.filter((_, idx) => idx !== i))}>{t('workflow.settings.requiredDataBuilder.remove')}</Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setInputMapping([...inputMapping, { param: '', expr: '' }])}>{t('workflow.settings.requiredDataBuilder.addMapping')}</Button>
              </div>
            )}
          </div>
        </div>

        {onCapturedEnabled && (
          <div className="flex gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-xs font-semibold flex items-center justify-center">3</span>
            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-ui-text">{t('workflow.settings.requiredDataBuilder.handleTheResult')}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode('wait_for_it')} className={stepCardClass(mode === 'wait_for_it')}>
                  <p className="text-xs font-semibold text-ui-text">{t('workflow.settings.requiredDataBuilder.waitForIt')}</p>
                  <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.waitForItHint')}</p>
                </button>
                <button type="button" onClick={() => setMode('fire_and_forget')} className={stepCardClass(mode === 'fire_and_forget')}>
                  <p className="text-xs font-semibold text-ui-text">{t('workflow.settings.requiredDataBuilder.fireAndForget')}</p>
                  <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.fireAndForgetHint')}</p>
                </button>
              </div>
              {mode === 'wait_for_it' && (
                <div>
                  <Label>{t('workflow.settings.requiredDataBuilder.onFailure')}</Label>
                  <Select value={onFailure} onChange={(e) => setOnFailure(e.target.value as 'block' | 'warn' | 'ignore')}>
                    <option value="block">{t('workflow.settings.requiredDataBuilder.blockAndShowError')}</option>
                    <option value="warn">{t('workflow.settings.requiredDataBuilder.warnAndProceed')}</option>
                    <option value="ignore">{t('workflow.settings.requiredDataBuilder.ignore')}</option>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label>{t('workflow.settings.requiredDataBuilder.retries')}</Label>
                  <Input
                    type="number" min={0} max={5} value={maxRetries}
                    onChange={(e) => setMaxRetries(Math.min(Math.max(Number(e.target.value) || 0, 0), 5))}
                  />
                </div>
                <div className="flex-1">
                  <Label>{t('workflow.settings.requiredDataBuilder.backoff')}</Label>
                  <Select value={backoff} onChange={(e) => setBackoff(e.target.value as 'exponential' | 'fixed')}>
                    <option value="exponential">{t('workflow.settings.requiredDataBuilder.exponential')}</option>
                    <option value="fixed">{t('workflow.settings.requiredDataBuilder.fixed')}</option>
                  </Select>
                </div>
              </div>
              <div className="rounded-control bg-ui-subtle border border-ui-border p-2">
                <p className="text-micro uppercase tracking-wide text-ui-text3 mb-1">{t('workflow.settings.requiredDataBuilder.howItIsImplemented')}</p>
                <p className="text-xs text-ui-text3">
                  {t('workflow.settings.requiredDataBuilder.howItIsImplementedBody')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────── Add Governance Condition modal ───────────────

// Serves both "add" and "edit". Editing matters more than it sounds: a gate carries a
// failure_message, a required_next_action and (now) the bound module's own settings, so the
// remove-and-recreate workaround silently drops the first two and re-derives gate_code from the
// name — renaming the gate that tenant forks and history already reference.
const AddGateModal: React.FC<{
  approvalTypes: ApprovalType[];
  /** The gate being edited, or undefined to create a new one. */
  initial?: GovernanceGate;
  onClose: () => void;
  onAdd: (g: GovernanceGate) => void;
}> = ({ approvalTypes, initial, onClose, onAdd }) => {
  const { t } = useTranslation();
  const isEdit = !!initial;
  const initialConfig = (initial?.condition_config ?? {}) as Record<string, unknown>;
  const [name, setName] = useState(initial?.description || initial?.gate_code || '');
  const [targetRef, setTargetRef] = useState(initial?.target_ref || '');
  const [conditionType, setConditionType] = useState(initial?.condition_type || CONDITION_TYPES[0].value);
  const [classification, setClassification] = useState<'blocking' | 'warning'>(
    initial?.classification === 'warning' ? 'warning' : 'blocking',
  );
  const [approvalTypeCode, setApprovalTypeCode] = useState(String(initialConfig.approval_type ?? ''));
  const [approverRole, setApproverRole] = useState(String(initialConfig.approver_role ?? ''));
  const [prohibitSelfApproval, setProhibitSelfApproval] = useState(!!initialConfig.prohibit_self_approval);
  const [failureMessage, setFailureMessage] = useState(initial?.failure_message || '');
  // WHAT TO DO about the block, as opposed to what is wrong. Never authorable before, so a gate
  // created through this modal had none — which is how assessment_meeting_required lost the
  // seeded "Schedule the assessment meeting." with no way to put it back.
  const [requiredNextAction, setRequiredNextAction] = useState(initial?.required_next_action || '');

  // Reverses the `capture` map the save function writes, so editing a required_data gate shows
  // the rows the admin authored instead of a blank builder that would overwrite them on save.
  const initialCapture = (initialConfig.capture ?? {}) as Record<string, Record<string, unknown>>;
  const initialCaptureRows: CaptureFieldRow[] = Object.values(initialCapture).map((f) => {
    const params = (f.options_params ?? {}) as Record<string, unknown>;
    const staticOptions = (params.options ?? []) as Array<{ value?: unknown }>;
    return {
      ...emptyCaptureRow(),
      label: String(f.label ?? ''),
      input_type: String(f.input_type ?? 'text'),
      captured_by_role: String(f.captured_by_role ?? ''),
      options_source: String(f.options_source ?? ''),
      options_value: f.options_source === 'static'
        ? staticOptions.map((o) => String(o?.value ?? '')).filter(Boolean).join(', ')
        : String(params.role ?? ''),
      mandatory: !!f.mandatory,
      tenant_relabel_allowed: !!f.tenant_relabel_allowed,
    } as CaptureFieldRow;
  });
  const initialOnCaptured = (initialConfig.on_captured ?? {}) as Record<string, unknown>;
  const initialMapping = Object.entries(
    (initialOnCaptured.input_mapping ?? {}) as Record<string, string>,
  ).map(([param, expr]) => ({ param, expr }));

  const [captureFields, setCaptureFields] = useState<CaptureFieldRow[]>(
    initialCaptureRows.length > 0 ? initialCaptureRows : [emptyCaptureRow()],
  );
  const [onCapturedEnabled, setOnCapturedEnabled] = useState(!!initialOnCaptured.action_key);
  const [actionKey, setActionKey] = useState(String(initialOnCaptured.action_key ?? ''));
  const [inputMapping, setInputMapping] = useState<Array<{ param: string; expr: string }>>(
    initialMapping.length > 0 ? initialMapping : [{ param: '', expr: 'this.value' }],
  );
  const [mode, setMode] = useState<'wait_for_it' | 'fire_and_forget'>(
    initialOnCaptured.mode === 'wait_for_it' ? 'wait_for_it' : 'fire_and_forget',
  );
  const [onFailure, setOnFailure] = useState<'block' | 'warn' | 'ignore'>(
    (initialOnCaptured.on_failure as 'block' | 'warn' | 'ignore') ?? 'block',
  );
  const [maxRetries, setMaxRetries] = useState(Number(initialOnCaptured.max_retries ?? 3));
  const [backoff, setBackoff] = useState<'exponential' | 'fixed'>(
    initialOnCaptured.backoff === 'fixed' ? 'fixed' : 'exponential',
  );

  // required_business_capture — binds this gate to a governance-action registry key, with an
  // explicit opt-in for the frontend to render a capture form inline instead of just blocking.
  const [captureActionKey, setCaptureActionKey] = useState(String(initialConfig.action_key ?? ''));
  const [captureUiEnabled, setCaptureUiEnabled] = useState(!!initialConfig.capture_ui_enabled);
  // The module's own settings for THIS binding — e.g. which engagement roles an assessment
  // meeting invites (participant_fields). Stored on the gate, so a redesigned workflow carries
  // its own policy instead of EST hardcoding one.
  const [captureInputValues, setCaptureInputValues] = useState<Record<string, ModuleInputValue>>(
    // Stored values are already the right shape (a list input round-trips as an array), so they
    // seed the form directly rather than being re-parsed from text.
    (initialConfig.input_values as Record<string, ModuleInputValue>) ?? {},
  );
  const { data: actionsData } = useListGovernanceActionsQuery();
  const captureActions = actionsData?.data ?? [];

  const canSave = name.trim() && targetRef.trim()
    && (conditionType !== 'required_data' || captureFields.some((f) => f.label.trim()))
    && (conditionType !== 'required_business_capture' || captureActionKey);

  const selectApprovalType = (code: string) => {
    setApprovalTypeCode(code);
    const match = approvalTypes.find((a) => a.approval_type_code === code);
    if (match) {
      setApproverRole(match.approver_role);
      setProhibitSelfApproval(!match.self_approval_allowed);
    }
  };

  const save = () => {
    if (!canSave) return;
    let conditionConfig: Record<string, unknown> = {};
    if (conditionType === 'approval') {
      conditionConfig = { approval_type: approvalTypeCode || undefined, approver_role: approverRole || undefined, prohibit_self_approval: prohibitSelfApproval };
    } else if (conditionType === 'required_data') {
      const rows = captureFields.filter((f) => f.label.trim());
      conditionConfig = {
        fields: rows.map((f) => slug(f.label)),
        capture: Object.fromEntries(rows.map((f) => [slug(f.label), {
          input_type: f.input_type,
          label: f.label.trim(),
          captured_by_role: f.captured_by_role || undefined,
          options_source: f.options_source || undefined,
          options_params: f.options_source === 'static'
            ? { options: f.options_value.split(',').map((v) => v.trim()).filter(Boolean).map((v) => ({ value: v, label: v })) }
            : f.options_source === 'role_lookup'
            ? { role: f.options_value }
            : {},
          mandatory: f.mandatory,
          tenant_relabel_allowed: f.tenant_relabel_allowed,
        }])),
      };
      if (onCapturedEnabled && actionKey) {
        conditionConfig.on_captured = {
          action_key: actionKey,
          input_mapping: Object.fromEntries(inputMapping.filter((m) => m.param.trim() && m.expr.trim()).map((m) => [m.param.trim(), m.expr.trim()])),
          mode,
          on_failure: onFailure,
          max_retries: maxRetries,
          backoff,
        };
      }
    } else if (conditionType === 'required_business_capture') {
      const inputValues = cleanModuleInputs(captureInputValues);
      conditionConfig = {
        action_key: captureActionKey,
        capture_ui_enabled: captureUiEnabled,
        ...(Object.keys(inputValues).length > 0 ? { input_values: inputValues } : {}),
      };
    }
    onAdd({
      ...initial,
      // gate_code is IDENTITY, not a label. Re-deriving it from the name on edit would rename the
      // gate that tenant forks, transition history and any required_next_action copy already
      // reference — so it is only generated when creating.
      gate_code: initial?.gate_code ?? slug(name),
      scope: initial?.scope ?? 'transition',
      target_ref: targetRef.trim(),
      classification,
      condition_type: conditionType,
      condition_config: conditionConfig,
      failure_message: failureMessage.trim() || undefined,
      required_next_action: requiredNextAction.trim() || undefined,
      description: name.trim(),
      is_optional: initial?.is_optional ?? false,
    });
    onClose();
  };

  return (
    <Modal
      title={isEdit ? 'Edit governance condition' : 'Add a governance condition'}
      onClose={onClose}
      onSave={save}
      saveLabel={isEdit ? 'Save condition' : 'Add condition'}
      saveDisabled={!canSave}
      maxWidthClassName={conditionType === 'required_data' ? 'max-w-4xl' : 'max-w-lg'}
      footerLeft={conditionType === 'required_data' ? t('workflow.settings.requiredDataBuilder.footerRecheckNote') : undefined}
    >
      <div>
        <Label required>Condition name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QA sign-off approval" />
      </div>
      <div>
        <Label required>Applies to (transition or status code)</Label>
        <Input value={targetRef} onChange={(e) => setTargetRef(e.target.value)} placeholder="e.g. qa_inspection_to_job_completed" />
      </div>
      <div>
        <Label required>Condition type</Label>
        <Select value={conditionType} onChange={(e) => setConditionType(e.target.value)}>
          {CONDITION_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Enforcement</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'blocking'} onChange={() => setClassification('blocking')} /> Blocking — cannot proceed
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'warning'} onChange={() => setClassification('warning')} /> Warning — ack to proceed
          </label>
        </div>
      </div>
      <div>
        <Label>Failure message (shown to the user when blocked)</Label>
        <Input
          value={failureMessage}
          onChange={(e) => setFailureMessage(e.target.value)}
          placeholder="e.g. Needs QA sign-off before this can move forward"
        />
        <p className="mt-1 text-xs text-ui-text3">
          Leave blank to use a generic default — but a specific message here is what SAM reads back to the user
          when this move is blocked, so it&apos;s worth writing one.
        </p>
      </div>
      <div>
        <Label>Next action (what the user should do)</Label>
        <Input
          value={requiredNextAction}
          onChange={(e) => setRequiredNextAction(e.target.value)}
          placeholder="e.g. Schedule the assessment meeting."
        />
        <p className="mt-1 text-xs text-ui-text3">
          Shown alongside the block and read by SAM, which is instructed to use the exact field names
          it mentions. The message above says what is WRONG; this says what to DO.
        </p>
      </div>

      {conditionType === 'required_data' && (
        <RequiredDataCaptureBuilder
          captureFields={captureFields} setCaptureFields={setCaptureFields}
          onCapturedEnabled={onCapturedEnabled} setOnCapturedEnabled={setOnCapturedEnabled}
          actionKey={actionKey} setActionKey={setActionKey}
          inputMapping={inputMapping} setInputMapping={setInputMapping}
          mode={mode} setMode={setMode}
          onFailure={onFailure} setOnFailure={setOnFailure}
          maxRetries={maxRetries} setMaxRetries={setMaxRetries}
          backoff={backoff} setBackoff={setBackoff}
        />
      )}

      {conditionType === 'required_business_capture' && (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Business action</p>
          <div>
            <Label required>Business module action</Label>
            {/* The gate's satisfaction check dispatches through the action registry, so
                "action" is the hard requirement. "capture" is only about whether the inline-form
                option below can render anything. */}
            <ModulePicker
              capability="action"
              value={captureActionKey}
              onChange={setCaptureActionKey}
              annotate={(m) => (hasCapability(m, 'capture') ? ' (has capture form)' : '')}
            />
            <p className="mt-1 text-xs text-ui-text3">
              The transition blocks until this business module reports the action as done for the engagement
              (e.g. a meeting exists).
            </p>
          </div>
          <ModuleInputFields
            module={captureActions.find((a) => a.action_key === captureActionKey)}
            values={captureInputValues}
            onChange={setCaptureInputValues}
          />

          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={captureUiEnabled} onChange={(e) => setCaptureUiEnabled(e.target.checked)} />
            Show a capture form inline instead of just blocking
          </label>
          {captureUiEnabled && captureActionKey && !captureActions.some((a) => a.action_key === captureActionKey && hasCapability(a, 'capture')) && (
            <p className="text-xs text-warning-text">
              No frontend form is registered for this action yet — the transition will still block on this
              condition, but nothing will render inline until one is added.
            </p>
          )}
        </div>
      )}

      {conditionType === 'approval' && (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Approval settings</p>
          {approvalTypes.length > 0 && (
            <div>
              <Label>Approval type (optional)</Label>
              <Select value={approvalTypeCode} onChange={(e) => selectApprovalType(e.target.value)}>
                <option value="">Custom — set role manually below</option>
                {approvalTypes.map((a) => <option key={a.approval_type_code} value={a.approval_type_code}>{a.name}</option>)}
              </Select>
            </div>
          )}
          <div>
            <Label>Approver role</Label>
            <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={prohibitSelfApproval} onChange={(e) => setProhibitSelfApproval(e.target.checked)} /> Prohibit self-approval
          </label>
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────── Add Approval Type modal ──────────────────────
// WFL-CFG-PLT-03-004 — a named, reusable approval type (e.g. "Proposal Review", "QA Sign-off")
// with a default approver role. Governance conditions with condition_type='approval' reference
// one of these by code instead of re-typing an approver role on every gate.

const AddApprovalTypeModal: React.FC<{
  onClose: () => void;
  onAdd: (a: ApprovalType) => void;
}> = ({ onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [approverRole, setApproverRole] = useState(ROLE_OPTIONS[0]);
  const [selfApprovalAllowed, setSelfApprovalAllowed] = useState(true);
  const [exposesCorrective, setExposesCorrective] = useState(false);
  const [description, setDescription] = useState('');

  const canSave = name.trim() && approverRole;

  const save = () => {
    if (!canSave) return;
    onAdd({
      approval_type_code: slug(name),
      name: name.trim(),
      approver_role: approverRole,
      self_approval_allowed: selfApprovalAllowed,
      exposes_corrective_on_reject: exposesCorrective,
      description: description.trim(),
    });
    onClose();
  };

  return (
    <Modal title="Add an approval type" onClose={onClose} onSave={save} saveLabel="Add approval type" saveDisabled={!canSave}>
      <div>
        <Label required>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Proposal Review" />
      </div>
      <div>
        <Label required>Approver role</Label>
        <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={selfApprovalAllowed} onChange={(e) => setSelfApprovalAllowed(e.target.checked)} /> Self-approval allowed
      </label>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={exposesCorrective} onChange={(e) => setExposesCorrective(e.target.checked)} /> Rejection exposes a corrective transition
      </label>
      <div>
        <Label>Description (optional)</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When this approval type applies" />
      </div>
    </Modal>
  );
};

// ─────────────────────────── Add Automation Hook modal ────────────────────

const AddHookModal: React.FC<{
  approvalTypes: ApprovalType[];
  onClose: () => void;
  onAdd: (h: AutomationHook) => void;
}> = ({ approvalTypes, onClose, onAdd }) => {
  // Same registry-backed query the Required Data gate builder above uses (GET
  // /workflows/governance-actions) — RTK Query dedupes this against that call, so no extra
  // network request just because two components both ask for it.
  const [triggerPoint, setTriggerPoint] = useState(TRIGGER_POINTS[0].value);
  const [triggerRef, setTriggerRef] = useState('');
  const [actionType, setActionType] = useState(ACTION_TYPES[0].value);
  const [execution, setExecution] = useState<'immediate' | 'background'>('immediate');
  const [mandatory, setMandatory] = useState(false);
  const [approvalTypeCode, setApprovalTypeCode] = useState('');
  const [approverRole, setApproverRole] = useState('');
  const [moduleActionKey, setModuleActionKey] = useState('');
  // Automation-hook input_values are passed to the handler LITERALLY (app.domain.workflows.
  // service._dispatch_hook: `input_values=config.get("input_values") or {}`, no expression
  // evaluation) — unlike the Required Data gate's on_captured input_mapping below, which DOES
  // evaluate each value as an expression against {this, engagement}.
  //
  // Keyed by the module's DECLARED input names rather than free-text rows. The old UI asked the
  // admin to type both the param name and the value from memory, so a typo or a forgotten
  // required field (`wbs_code` on estimation.generateRecommendedEstimate) saved cleanly and then
  // failed on every fire, recorded only in workflow_automation_outcomes.
  const [moduleInputValues, setModuleInputValues] = useState<Record<string, string>>({});

  const isApproval = actionType === 'approval_creation';
  const isModuleAction = actionType === 'module_action';
  // Required admin-suppliable inputs must be filled before saving — the backend enforces the
  // same rule (module_binding_validation), this just stops the round trip.
  // Same list the ModulePicker renders; needed here too because the input form is driven by
  // the SELECTED module's contract, not by the picker.
  const { data: moduleOptionsData } = useListGovernanceActionsQuery();
  const moduleOptions: GovernanceActionOption[] = moduleOptionsData?.data ?? [];
  const selectedModule = moduleOptions.find((m) => m.action_key === moduleActionKey);
  const adminInputs = (selectedModule?.module_inputs ?? []).filter((i) => !i.runtime);
  const missingRequired = adminInputs
    .filter((i) => i.required && !(moduleInputValues[i.name] ?? '').trim())
    .map((i) => i.name);
  const canSave = triggerRef.trim()
    && (!isApproval || approverRole)
    && (!isModuleAction || (moduleActionKey && missingRequired.length === 0));

  const save = () => {
    if (!canSave) return;
    const actionConfig = isApproval
      ? { approval_type: approvalTypeCode || undefined, approver_role: approverRole, approvers: [{ role: approverRole }] }
      : isModuleAction
      ? {
          action_key: moduleActionKey,
          // Blank entries are omitted rather than sent as "" — the action's own `or default`
          // fallbacks should apply, and an empty string would defeat them.
          input_values: Object.fromEntries(
            Object.entries(moduleInputValues).filter(([, v]) => v.trim() !== ''),
          ),
        }
      : { execution };
    onAdd({
      hook_code: `${slug(triggerRef)}_${isModuleAction ? slug(moduleActionKey) : actionType}`,
      trigger_point: triggerPoint,
      trigger_ref: triggerRef.trim(),
      action_type: actionType,
      action_config: actionConfig,
      is_mandatory: mandatory,
      is_optional: !mandatory,
      description: '',
    });
    onClose();
  };

  return (
    <Modal title="Add an automation hook" onClose={onClose} onSave={save} saveLabel="Add hook" saveDisabled={!canSave}>
      <div>
        <Label required>Trigger point</Label>
        <Select value={triggerPoint} onChange={(e) => setTriggerPoint(e.target.value)}>
          {TRIGGER_POINTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Bound to (status or transition code)</Label>
        <Input value={triggerRef} onChange={(e) => setTriggerRef(e.target.value)} placeholder="e.g. invoice_preparation" />
      </div>
      <div>
        <Label required>Action type — registered actions only</Label>
        <Select value={actionType} onChange={(e) => setActionType(e.target.value)}>
          {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </Select>
      </div>

      {isApproval ? (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Approval settings</p>
          <p className="text-xs text-ui-text3">
            This is what actually creates the pending approval request when the trigger fires — without this, a
            governance gate referencing this approval type will block forever with nothing to approve.
          </p>
          {approvalTypes.length > 0 && (
            <div>
              <Label>Approval type (optional)</Label>
              <Select value={approvalTypeCode} onChange={(e) => setApprovalTypeCode(e.target.value)}>
                <option value="">Custom — set role manually below</option>
                {approvalTypes.map((a) => <option key={a.approval_type_code} value={a.approval_type_code}>{a.name}</option>)}
              </Select>
            </div>
          )}
          <div>
            <Label required>Approver role</Label>
            <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
            </Select>
          </div>
        </div>
      ) : isModuleAction ? (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Module action settings</p>
          <div>
            <Label required>Action — registered actions only</Label>
            {/* Was unfiltered — every module showed here, so a display-only card could be
                wired as a hook and would no-op forever with no error anywhere. */}
            <ModulePicker capability="action" value={moduleActionKey} onChange={setModuleActionKey} />
          </div>
          {selectedModule && !selectedModule.hook_bindable && (
            <p className="text-xs text-danger-text">
              This module cannot run as a hook: a required input is collected from the user at
              capture time, so there is no fixed value for it. Bind it as a capture gate on a
              transition instead.
            </p>
          )}

          {/* Retry safety. A non-mandatory hook is retried per
              workflow_engine.automation_max_retries, so a non-idempotent action repeats its side
              effect — three tasks, or three emails to a real customer. Warned, not blocked:
              accepting that risk is the admin's call, not ours. */}
          {selectedModule && !selectedModule.is_idempotent && !mandatory && (
            <p className="text-xs text-warning-text">
              {selectedModule.name} is not idempotent — if it fails and retries, its effect happens
              more than once. Mark the hook mandatory to stop retries.
            </p>
          )}

          {selectedModule && adminInputs.length > 0 && (
            <div>
              <Label>Input values (fixed, not expressions)</Label>
              <p className="text-xs text-ui-text3 mb-1.5">
                Passed to the action exactly as typed, every time it fires. Not evaluated against the
                engagement, unlike the Required Data builder's mappings below.
              </p>
              {adminInputs.map((input) => {
                const value = moduleInputValues[input.name] ?? '';
                const setValue = (v: string) =>
                  setModuleInputValues({ ...moduleInputValues, [input.name]: v });
                const isMissing = input.required && !value.trim();
                return (
                  <div key={input.name} className="mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-40 shrink-0 font-mono text-xs ${isMissing ? 'text-danger-text' : 'text-ui-text2'}`}>
                        {input.name}{input.required ? ' *' : ''}
                      </span>
                      {input.allowed_values.length > 0 ? (
                        <Select value={value} onChange={(e) => setValue(e.target.value)}>
                          <option value="">{input.default != null ? String(input.default) : '—'}</option>
                          {input.allowed_values.map((v) => (
                            <option key={String(v)} value={String(v)}>{String(v)}</option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          type={input.type === 'int' || input.type === 'float' ? 'number' : 'text'}
                          /* The default is the placeholder, so leaving it blank visibly shows what
                             the action will fall back to instead of being a guess. */
                          placeholder={input.default != null ? `default: ${String(input.default)}` : input.type}
                          className="flex-1"
                        />
                      )}
                    </div>
                    {input.description && (
                      <p className="ml-40 pl-2 text-xs text-ui-text3">{input.description}</p>
                    )}
                  </div>
                );
              })}
              {missingRequired.length > 0 && (
                <p className="text-xs text-danger-text">
                  Required: {missingRequired.join(', ')}. Without it the hook saves but fails every
                  time it fires.
                </p>
              )}
            </div>
          )}

          {/* Runtime-collected inputs are listed read-only so the admin can see what the module
              needs without being invited to hardcode it. */}
          {selectedModule && selectedModule.module_inputs.some((i) => i.runtime) && (
            <div>
              <Label>Collected at runtime</Label>
              <p className="text-xs text-ui-text3">
                {selectedModule.module_inputs.filter((i) => i.runtime).map((i) => i.name).join(', ')}
                {' '}— supplied by the user or resolved from the engagement when the module runs, not set here.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <Label>Execution</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="radio" checked={execution === 'immediate'} onChange={() => setExecution('immediate')} /> Immediate — inline post-commit
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="radio" checked={execution === 'background'} onChange={() => setExecution('background')} /> Background → SBP, retries
            </label>
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} /> Mandatory (must succeed to finalize)
      </label>
      <p className="text-xs text-ui-text3">
        WFL records a minimum outcome (action · occurrence · success/fail). No tenant scripts, formulas, or external API calls — execution is owned by the target module & SBP.
      </p>
    </Modal>
  );
};

// ─────────────────────────── Add module display (POST binding) ────────────
// WFL business-module linking, POST side — no blocking, just "show this module's card once the
// engagement reaches this status" (ModuleDisplayDefinition on the backend). The PRE side (capture
// form before a transition) is a required_business_capture governance condition instead — see
// AddGateModal above. Both status and module are real dropdowns, not free text, so an admin can't
// typo a status_code or reference an unregistered module.
const AddModuleDisplayModal: React.FC<{
  statuses: Array<{ status_code: string; name: string }>;
  initialStatusCode?: string;
  onClose: () => void;
  onAdd: (m: ModuleDisplay) => void;
}> = ({ statuses, initialStatusCode, onClose, onAdd }) => {
  const [statusCode, setStatusCode] = useState(initialStatusCode ?? statuses[0]?.status_code ?? '');
  const [moduleCode, setModuleCode] = useState('');
  const [description, setDescription] = useState('');
  const canSave = statusCode && moduleCode;

  const save = () => {
    if (!canSave) return;
    onAdd({
      binding_code: slug(`${statusCode}_${moduleCode}_display`),
      scope: 'status_entry',
      target_ref: statusCode,
      module_code: moduleCode,
      is_active: true,
      description: description.trim() || undefined,
    });
    onClose();
  };

  return (
    <Modal title="Add module to status" onClose={onClose} onSave={save} saveLabel="Add module" saveDisabled={!canSave} maxWidthClassName="max-w-md">
      <div>
        <Label required>Status</Label>
        <Select value={statusCode} onChange={(e) => setStatusCode(e.target.value)}>
          {statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Business module</Label>
        <ModulePicker capability="display" value={moduleCode} onChange={setModuleCode} />
        <p className="mt-1 text-xs text-ui-text3">
          Only modules with a registered display card are shown here.
        </p>
      </div>
      <div>
        <Label>Description (optional)</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Shows the scheduled meeting" />
      </div>
    </Modal>
  );
};

// ─────────────────────────── Main page ────────────────────────────────────

const WorkflowGovernancePage: React.FC = () => {
  const { t } = useTranslation();
  const { definitionId, version } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useGetWorkflowDefinitionQuery({ definition_id: definitionId!, version: Number(version) });
  const [updateDraft, { isLoading: saving }] = useUpdateWorkflowDefinitionMutation();

  const [gates, setGates] = useState<GovernanceGate[]>([]);
  const [editingGate, setEditingGate] = useState<GovernanceGate | null>(null);
  // Two-step delete, keyed by gate_code. Deliberately NOT a bare one-click filter like the
  // module-display Remove beside it: deleting a blocking gate removes a GUARD, so a transition
  // that was blocked yesterday goes through today — and for a capture gate it silently takes the
  // bound module and that module's own settings (participant_fields and the like) with it.
  // Nothing is written until Save Changes, so this is a slip-guard rather than a safety net.
  const [confirmDeleteGate, setConfirmDeleteGate] = useState<string | null>(null);
  const [approvalTypes, setApprovalTypes] = useState<ApprovalType[]>([]);
  const [hooks, setHooks] = useState<AutomationHook[]>([]);
  const [moduleDisplays, setModuleDisplays] = useState<ModuleDisplay[]>([]);
  const [showAddGate, setShowAddGate] = useState(false);
  const [showAddApprovalType, setShowAddApprovalType] = useState(false);
  const [showAddHook, setShowAddHook] = useState(false);
  const [addDisplayForStatus, setAddDisplayForStatus] = useState<string | null>(null);
  const [error, setError] = useState('');

  const { data: businessModulesData } = useListGovernanceActionsQuery();
  const businessModules = businessModulesData?.data ?? [];

  useEffect(() => {
    if (!data?.data) return;
    setGates(data.data.governance_gates as GovernanceGate[]);
    setApprovalTypes((data.data.approval_types ?? []) as ApprovalType[]);
    setHooks(data.data.automation_hooks as AutomationHook[]);
    setModuleDisplays((data.data.module_displays ?? []) as ModuleDisplay[]);
  }, [data]);

  const def = data?.data;
  const isDraft = def?.status === 'draft';

  const handleSave = async () => {
    if (!def) return;
    setError('');
    try {
      const body: CreateWorkflowRequest = {
        name: def.name,
        description: def.description,
        engagement_types: def.engagement_types,
        stages: def.stages,
        processes: def.processes,
        statuses: def.statuses,
        entry_points: def.entry_points,
        transitions: def.transitions,
        governance_gates: gates,
        approval_types: approvalTypes,
        automation_hooks: hooks,
        authority_rules: def.authority_rules,
        module_displays: moduleDisplays,
      };
      await updateDraft({ definition_id: def.definition_id, version: def.version, body }).unwrap();
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setError(err?.data?.message ?? 'Failed to save.');
    }
  };

  if (isLoading || !def) {
    return <PageShell header={null}><LoadingState label="Loading…" className="py-20" /></PageShell>;
  }

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          back={
            <Button variant="link" size="xs" onClick={() => navigate(`/platform/workflows/${definitionId}/${version}/edit`)}>
              ← Back to builder
            </Button>
          }
          title="Governance & Automation Hooks"
          subtitle={`${def.name} /`}
          actions={
            <div className="flex items-center gap-2">
              {error && <p className="text-xs text-danger-text">{error}</p>}
              {isDraft && <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>}
            </div>
          }
        />
      }
    >
      {/* ── Business modules catalogue — read-only list of every registered module_code this
          workflow's gates/status displays/hooks can bind to (app/db/seed/sc/business_modules_seed.py).
          Registering a new one is a code change; picking one for THIS workflow happens in the
          "+ Add condition" / "+ Add module" (status display) / "+ Add hook" modals below, which
          all draw on this same list. */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-semibold text-ui-text">Business modules</h2>
          <Badge variant="neutral">{businessModules.length}</Badge>
        </div>
        <p className="text-xs text-ui-text3 mb-3">
          Platform-wide catalogue any workflow can bind to — as a governance condition (capture form before a
          transition), a status display (card after), an on_captured action, or an automation hook.
        </p>
        <div className="table-card">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th-cell">Module</th>
                <th className="th-cell">Category</th>
                <th className="th-cell">Can be bound as</th>
              </tr>
            </thead>
            <tbody>
              {businessModules.map((m) => (
                <tr key={m.action_key}>
                  <td className="td-cell">
                    <span className="font-medium">{m.name || m.action_key}</span>
                    <span className="block font-mono text-xs text-ui-text3">{m.action_key}</span>
                    {m.description && <span className="block text-xs text-ui-text3">{m.description}</span>}
                  </td>
                  <td className="td-cell text-ui-text2">{m.category || '—'}</td>
                  <td className="td-cell">
                    <span className="flex flex-wrap gap-1">
                      {CAPABILITY_ORDER.filter((cap) => hasCapability(m, cap)).map((cap) => (
                        <Badge key={cap} variant="success" className="text-micro uppercase">
                          {CAPABILITY_LABELS[cap]}
                        </Badge>
                      ))}
                      {(m.capabilities ?? []).length === 0 && (
                        <Badge variant="neutral" className="text-micro uppercase">none</Badge>
                      )}
                    </span>
                    {/* Catalogue-vs-registry disagreement, computed server-side. Surfaced here as
                        well as in the pickers because this table is where an admin comes to ask
                        "why can't I bind this?". */}
                    {m.capability_drift?.map((d) => (
                      <span key={d} className="mt-1 block text-xs text-warning-text">⚠ {d}</span>
                    ))}
                  </td>
                </tr>
              ))}
              {businessModules.length === 0 && (
                <tr><td colSpan={3} className="td-cell py-4 text-center text-ui-text3">No business modules registered yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* ── Left column: Governance conditions + Approval Types ─────────── */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-ui-text">Governance conditions</h2>
                <Badge variant="neutral">{gates.length}</Badge>
              </div>
              {isDraft && <Button size="sm" onClick={() => setShowAddGate(true)}>+ Add condition</Button>}
            </div>
            <div className="space-y-1.5">
              {gates.map((g) => {
                // Surfaced on the row because it is the one thing an admin cannot see otherwise:
                // which business module this gate binds, and the settings it passes to it.
                const boundModule = (g.condition_config as Record<string, unknown> | undefined)?.action_key;
                const boundInputs = (g.condition_config as Record<string, unknown> | undefined)?.input_values as
                  Record<string, unknown> | undefined;
                return (
                  <div key={g.gate_code} className="flex items-center justify-between gap-2 card-surface px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-ui-text truncate">{g.description || g.gate_code}</p>
                      <p className="text-xs text-ui-text3">on {g.target_ref} · type: {g.condition_type.replace(/_/g, ' ')}</p>
                      {!!boundModule && (
                        <p className="text-xs text-ui-text3 truncate">
                          module: <span className="font-mono">{String(boundModule)}</span>
                          {boundInputs && Object.keys(boundInputs).length > 0
                            && ` · ${Object.entries(boundInputs)
                              .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('/') : String(v)}`)
                              .join(', ')}`}
                        </p>
                      )}
                      {/* Named specifically, because the consequences of deleting a gate are not
                          visible from its own row: the guard it enforces, and for a capture gate
                          the bound module plus the settings configured against it. Recreating it
                          later means retyping the failure message and next action too — the fields
                          the Add modal starts blank. */}
                      {confirmDeleteGate === g.gate_code && (
                        <p className="mt-1 text-xs text-danger-text">
                          {g.classification === 'blocking'
                            ? t('workflow.settings.gates.deleteWarnBlocking', 'Deleting this removes a blocking guard — the transition will go through without it.')
                            : t('workflow.settings.gates.deleteWarnWarning', 'Deleting this removes a warning — nothing will flag this case.')}
                          {!!boundModule && ` ${t('workflow.settings.gates.deleteWarnModule', 'It also unbinds {{module}} and the settings configured against it here.', { module: String(boundModule) })}`}
                          {' '}
                          {t('workflow.settings.gates.deleteWarnWhen', 'Takes effect when you Save Changes.')}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant={g.classification === 'blocking' ? 'danger' : 'warning'} className="text-micro uppercase">{g.classification}</Badge>
                      {isDraft && confirmDeleteGate !== g.gate_code && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => setEditingGate(g)}>
                            {t('workflow.settings.gates.edit', 'Edit')}
                          </Button>
                          <Button variant="destructive-ghost" size="sm" onClick={() => setConfirmDeleteGate(g.gate_code)}>
                            {t('workflow.settings.gates.delete', 'Delete')}
                          </Button>
                        </>
                      )}
                      {isDraft && confirmDeleteGate === g.gate_code && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => setConfirmDeleteGate(null)}>
                            {t('workflow.settings.gates.cancelDelete', 'Cancel')}
                          </Button>
                          <Button
                            variant="destructive-ghost"
                            size="sm"
                            onClick={() => {
                              setGates((prev) => prev.filter((x) => x.gate_code !== g.gate_code));
                              setConfirmDeleteGate(null);
                            }}
                          >
                            {t('workflow.settings.gates.confirmDelete', 'Confirm delete')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {gates.length === 0 && <EmptyState variant="inline" className="py-4" title="No governance conditions yet." />}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-ui-text">Approval types</h2>
                <Badge variant="neutral">{approvalTypes.length}</Badge>
              </div>
              {isDraft && <Button size="sm" onClick={() => setShowAddApprovalType(true)}>+ Add approval type</Button>}
            </div>
            <div className="space-y-1.5">
              {approvalTypes.map((a) => (
                <div key={a.approval_type_code} className="flex items-center justify-between card-surface px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-ui-text truncate">{a.name}</p>
                    <p className="text-xs text-ui-text3">approver role: {humanize(a.approver_role)}{a.exposes_corrective_on_reject ? ' · reject → exposes corrective transition' : ''}</p>
                  </div>
                  {!a.self_approval_allowed && <Badge variant="neutral" className="text-micro uppercase shrink-0">No self-approval</Badge>}
                </div>
              ))}
              {approvalTypes.length === 0 && <EmptyState variant="inline" className="py-4" title="No approval types yet." />}
            </div>
          </div>
        </div>

        {/* ── Right column: Automation hooks ───────────────────────────────── */}
        <div>
          <p className="text-xs text-ui-text3 mb-3">Predefined actions only · background & scheduled → SBP</p>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-ui-text">Automation hooks</h2>
              <Badge variant="neutral">{hooks.length}</Badge>
            </div>
            {isDraft && <Button size="sm" onClick={() => setShowAddHook(true)}>+ Add hook</Button>}
          </div>
          <div className="space-y-1.5">
            {hooks.map((h) => (
              <div key={h.hook_code} className="flex items-center justify-between card-surface px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-ui-text truncate">{ACTION_TYPES.find((a) => a.value === h.action_type)?.label ?? h.action_type}</p>
                  <p className="text-xs text-ui-text3">{h.trigger_point.replace(/_/g, ' ')} · {h.trigger_ref}</p>
                </div>
                {h.is_mandatory && <Badge variant="neutral" className="text-micro uppercase shrink-0">Mandatory</Badge>}
              </div>
            ))}
            {hooks.length === 0 && <EmptyState variant="inline" className="py-4" title="No automation hooks yet." />}
          </div>
          <p className="text-xs text-ui-text3 mt-3">No tenant scripts, formulas, or external API calls. WFL keeps a minimum outcome record; execution owned by SBP.</p>
        </div>
      </div>

      {/* ── Status displays — WFL business-module linking, POST side. Grouped by status (not a
          flat list) since a status can carry more than one module at once (e.g. a meeting card
          AND an interactive capture tool both at Assessment Scheduled). Only statuses that
          currently have >=1 binding get their own group; "+ Add module to a status" covers the
          first binding for a status not shown yet. */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ui-text">Status displays</h2>
            <Badge variant="neutral">{moduleDisplays.length}</Badge>
          </div>
          {isDraft && <Button size="sm" onClick={() => setAddDisplayForStatus('')}>+ Add module to a status</Button>}
        </div>
        <p className="text-xs text-ui-text3 mb-3">
          No blocking, no capture form — once the engagement reaches the status, the bound module's card renders.
          The pre-transition equivalent (blocks + capture form) is a "Business action" governance condition above.
        </p>
        <div className="space-y-3">
          {Array.from(new Set(moduleDisplays.map((m) => m.target_ref))).map((statusCode) => {
            const statusName = def.statuses.find((s) => s.status_code === statusCode)?.name ?? statusCode;
            const bindings = moduleDisplays.filter((m) => m.target_ref === statusCode);
            return (
              <div key={statusCode} className="card-surface overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-ui-raised">
                  <span className="text-sm font-medium text-ui-text">{statusName}</span>
                  {isDraft && <Button variant="outline" size="sm" onClick={() => setAddDisplayForStatus(statusCode)}>+ Add module</Button>}
                </div>
                <div className="divide-y divide-ui-border2">
                  {bindings.map((m) => (
                    <div key={m.binding_code} className="flex items-center justify-between px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm text-ui-text truncate">{m.module_code}</p>
                        {m.description && <p className="text-xs text-ui-text3 truncate">{m.description}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {m.is_active === false && <Badge variant="neutral" className="text-micro uppercase">Inactive</Badge>}
                        {isDraft && (
                          <Button
                            variant="destructive-ghost"
                            size="sm"
                            onClick={() => setModuleDisplays((prev) => prev.filter((x) => x.binding_code !== m.binding_code))}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {moduleDisplays.length === 0 && (
            <EmptyState variant="inline" className="py-4" title="No status displays yet." />
          )}
        </div>
      </div>

      {showAddGate && <AddGateModal approvalTypes={approvalTypes} onClose={() => setShowAddGate(false)} onAdd={(g) => setGates((prev) => [...prev, g])} />}
      {editingGate && (
        <AddGateModal
          approvalTypes={approvalTypes}
          initial={editingGate}
          onClose={() => setEditingGate(null)}
          onAdd={(g) => setGates((prev) => prev.map((x) => (x.gate_code === g.gate_code ? g : x)))}
        />
      )}
      {showAddApprovalType && <AddApprovalTypeModal onClose={() => setShowAddApprovalType(false)} onAdd={(a) => setApprovalTypes((prev) => [...prev, a])} />}
      {showAddHook && <AddHookModal approvalTypes={approvalTypes} onClose={() => setShowAddHook(false)} onAdd={(h) => setHooks((prev) => [...prev, h])} />}
      {addDisplayForStatus !== null && (
        <AddModuleDisplayModal
          statuses={def.statuses}
          initialStatusCode={addDisplayForStatus || undefined}
          onClose={() => setAddDisplayForStatus(null)}
          onAdd={(m) => setModuleDisplays((prev) => [...prev, m])}
        />
      )}
    </PageShell>
  );
};

export default WorkflowGovernancePage;
